import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Children } from "react";

// Built-in Helvetica keeps the document metrically identical to Arial without
// parsing ~3.4MB of TTFs on the main thread every time the reader opens.
export const serviceAgreementFileName = "SERVICE AGREEMENT.pdf";

const value = (v, fallback = "-") =>
  v === undefined || v === null || v === "" ? fallback : String(v);

export const serviceAgreementSampleData = {
  date: "«DATE»",
  owner: {
    name: "«OWNER_NAME»",
    idNo: "«ID_NO__REG_NO»",
    mailingAddress: [
      "«MAILING_ADDRESS_1»",
      "«MAILING_ADDRESS_2»",
      "«MAILING_ADDRESS_3»",
    ],
    mobileNo: "«MOBILE_NO»",
  },
  vehicle: {
    make: "«MAKE»",
    model: "«MODEL»",
    registrationNo: "«CAR_NO»",
    engineCapacity: "«ENGINE_CAPACITY»",
    engineNo: "«ENGINE_NO»",
    yearOfManufacture: "«YEAR_OF_MANUFACTURE»",
    chassisNo: "«CHASSIS_NO»",
    mileage: "",
  },
  agreedSellingPrice: "«AGREED_SELLING_PRICE»",
  charges: {
    roadTax: "",
    insurance: "",
    workshopAccessories: "",
  },
  salesAgent: "«SALES_AGENT»",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 72,
    paddingRight: 72,
    paddingBottom: 54,
    paddingLeft: 72,
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.16,
    color: "#000000",
    backgroundColor: "#ffffff",
  },
  detailPage: { fontSize: 10, lineHeight: 1.08 },
  frontPage: { fontSize: 9, lineHeight: 1.08 },
  questionnairePage: { fontSize: 8, lineHeight: 1 },
  legalPage: {
    paddingRight: 36,
    paddingLeft: 50,
    fontSize: 8,
    lineHeight: 1.08,
  },
  body8: { fontSize: 8, lineHeight: 1.08 },
  body75: { fontSize: 7.5, lineHeight: 1.07 },
  body7: { fontSize: 7, lineHeight: 1.05 },
  legal: { fontSize: 8, lineHeight: 1, textAlign: "justify" },
  bold: { fontWeight: 700 },
  regular: { fontFamily: "Helvetica", fontWeight: 400 },
  italic: { fontStyle: "italic" },
  centered: { textAlign: "center" },
  title: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1,
  },
  subtitle: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: 11,
    marginTop: 1,
  },
  pageNo: {
    position: "absolute",
    bottom: 35,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
  },
  sectionHeading: {
    flexDirection: "row",
    fontSize: 10,
    fontWeight: 700,
    marginTop: 7,
    marginBottom: 10,
  },
  sectionLetter: { width: 36 },
  infoRow: {
    flexDirection: "row",
    minHeight: 14,
  },
  infoLabel: { width: 155.8, fontWeight: 700 },
  infoColon: { width: 14.95, fontWeight: 700 },
  infoValue: { flex: 1, fontWeight: 700 },
  fieldGap: { height: 7 },
  sectionGap: { height: 13 },
  p: { marginBottom: 8 },
  generalP: { marginBottom: 8, textAlign: "justify" },
  indentRow: { flexDirection: "row", marginBottom: 8 },
  indentNo: { width: 36 },
  indentText: { flex: 1, textAlign: "justify" },
  subIndentNo: { width: 18 },
  table: { borderTopWidth: 0.5, borderLeftWidth: 0.5 },
  serviceTable: { width: 431.5, marginLeft: 36 },
  feeTable: { width: 431.5, marginLeft: 36 },
  tr: { flexDirection: "row" },
  th: {
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    paddingVertical: 4,
    paddingHorizontal: 5,
    fontWeight: 700,
    textAlign: "center",
    justifyContent: "center",
    backgroundColor: "#D1D1D1",
  },
  td: {
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    paddingVertical: 4,
    paddingHorizontal: 5,
    justifyContent: "flex-start",
  },
  serviceNameCell: {
    width: 58.45,
    alignItems: "center",
    textAlign: "center",
  },
  serviceDetailCell: { width: 373.05 },
  serviceDetailRow: { flexDirection: "row" },
  serviceDetailMarker: { width: 20 },
  serviceDetailText: { flex: 1, textAlign: "justify" },
  feeCell: {
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  bullet: { flexDirection: "row", marginBottom: 2 },
  bulletMark: { width: 11 },
  bulletText: { flex: 1 },
  signatureGrid: { flexDirection: "row", marginTop: 28 },
  signatureCol: { width: "50%", paddingRight: 18 },
  signatureColRight: { width: "50%", paddingLeft: 18 },
  signatureLine: { borderTopWidth: 0.8, marginTop: 48, marginBottom: 4 },
  twoCol: { flexDirection: "row", gap: 22 },
  column: { flex: 1 },
  legalHeading: { fontSize: 8, fontWeight: 700, marginBottom: 5 },
  legalSection: {
    fontSize: 8,
    fontWeight: 700,
    marginTop: 5,
    marginBottom: 3,
  },
  legalPara: {
    fontSize: 8,
    lineHeight: 1.08,
    textAlign: "justify",
    marginBottom: 1,
  },
  noticeTable: { width: 197.65, marginLeft: 30.35 },
  noticeLabel: { width: 51.9, fontWeight: 700, backgroundColor: "#C1E4F5" },
  qHeading: {
    fontSize: 8,
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 2,
    marginLeft: 18,
    marginRight: -36,
    paddingTop: 2,
    borderTopWidth: 0.5,
    borderTopColor: "#000000",
  },
  qRow: { flexDirection: "row", marginBottom: 1 },
  qNo: { width: 18 },
  qText: { flex: 1 },
  qSub: { flexDirection: "row", marginLeft: 36, marginBottom: 1 },
  qBullet: { width: 18 },
});

function PageNumber({ n }) {
  return <Text style={styles.pageNo}>{n}</Text>;
}

function emphasizeDefinedTerms(children) {
  return Children.map(children, (child) => {
    if (typeof child !== "string") return child;

    return child
      .split(/([“"][^”"]+[”"])/g)
      .filter(Boolean)
      .map((part, index) =>
        /^[“"]/.test(part) ? (
          <Text style={styles.bold} key={`${part}-${index}`}>
            {part}
          </Text>
        ) : (
          part
        ),
      );
  });
}

function InfoRow({ label, children }) {
  return (
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoColon}>:</Text>
      <Text style={styles.infoValue}>{children}</Text>
    </View>
  );
}

function FrontSectionHeading({ letter, children }) {
  return (
    <View style={styles.sectionHeading}>
      <Text style={styles.sectionLetter}>({letter})</Text>
      <Text>{children}</Text>
    </View>
  );
}

function Numbered({ no, children, style }) {
  return (
    <View style={[styles.indentRow, style]}>
      <Text style={styles.indentNo}>{no}</Text>
      <Text style={styles.indentText}>{emphasizeDefinedTerms(children)}</Text>
    </View>
  );
}

function Bullet({ children }) {
  return (
    <View style={styles.bullet}>
      <Text style={styles.bulletMark}>•</Text>
      <Text style={styles.bulletText}>{children}</Text>
    </View>
  );
}

function ServiceNameCell({ children }) {
  return (
    <View style={[styles.td, styles.serviceNameCell]}>
      <Text style={styles.centered}>{children}</Text>
    </View>
  );
}

function ServiceDetail({ marker, children, style }) {
  return (
    <View style={[styles.serviceDetailRow, style]}>
      <Text style={styles.serviceDetailMarker}>{marker}</Text>
      <Text style={styles.serviceDetailText}>
        {emphasizeDefinedTerms(children)}
      </Text>
    </View>
  );
}

function FeeCell({ width, children }) {
  return (
    <View style={[styles.td, styles.feeCell, { width }]}>
      <Text style={styles.centered}>{children}</Text>
    </View>
  );
}

function LegalP({ children, bold }) {
  return (
    <Text style={[styles.legalPara, bold && styles.bold]}>
      {emphasizeDefinedTerms(children)}
    </Text>
  );
}

function LegalNumber({ no, children }) {
  return (
    <View style={{ flexDirection: "row", marginBottom: 1 }}>
      <Text style={{ width: 17, fontSize: 8, lineHeight: 1.08 }}>{no}</Text>
      <Text style={[styles.legalPara, { flex: 1, marginBottom: 0 }]}>
        {emphasizeDefinedTerms(children)}
      </Text>
    </View>
  );
}

function QuestionnaireItem({ n, text, sub }) {
  return (
    <>
      <View style={styles.qRow}>
        <Text style={styles.qNo}>{n}.</Text>
        <Text style={styles.qText}>{text}</Text>
      </View>
      {sub ? (
        <View style={styles.qSub}>
          <Text style={styles.qBullet}>o</Text>
          <Text style={styles.qText}>{sub}</Text>
        </View>
      ) : null}
    </>
  );
}

function PageOne({ data }) {
  const owner = data.owner || {};
  const vehicle = data.vehicle || {};
  const address = Array.isArray(owner.mailingAddress)
    ? owner.mailingAddress
    : [owner.mailingAddress];

  return (
    <Page size="LETTER" style={[styles.page, styles.detailPage]}>
      <Text style={styles.title}>SERVICE AGREEMENT</Text>
      <Text style={styles.subtitle}>(“Agreement”)</Text>

      <View style={{ marginTop: 17 }}>
        <InfoRow label="Date">{value(data.date)}</InfoRow>
      </View>

      <FrontSectionHeading letter="A">
        Vehicle owner’s details
      </FrontSectionHeading>
      <InfoRow label="Name">{value(owner.name)}</InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label={"Identification Number or\nRegistration Number"}>
        {value(owner.idNo)}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Mailing Address">{value(address[0])}</InfoRow>
      {address.slice(1).map((line, index) => (
        <InfoRow label="" key={index}>
          {value(line, "")}
        </InfoRow>
      ))}
      <View style={styles.fieldGap} />
      <InfoRow label="Mobile No.">{value(owner.mobileNo)}</InfoRow>

      <View style={styles.sectionGap} />
      <FrontSectionHeading letter="B">
        Vehicle’s descriptions
      </FrontSectionHeading>
      <InfoRow label="Make & Model">
        {value(vehicle.make)} {value(vehicle.model)}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Vehicle Registration No.">
        {value(vehicle.registrationNo)}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Engine Capacity">{value(vehicle.engineCapacity)}</InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Engine No.">{value(vehicle.engineNo)}</InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Year of Manufacture">
        {value(vehicle.yearOfManufacture)}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Chassis No.">{value(vehicle.chassisNo)}</InfoRow>
      <View style={styles.fieldGap} />

      <View style={[styles.infoRow, { marginTop: 1 }]}>
        <Text style={styles.infoLabel}>Vehicle State and Condition</Text>
        <Text style={styles.infoColon}>:</Text>
        <View style={{ flex: 1 }}>
          <Text style={[styles.regular, { marginBottom: 5 }]}>
            per the vehicle inspection report, a copy of which is annexed to
            this Agreement as Annexure 1 (
            <Text style={styles.bold}>“Report”</Text>).
          </Text>
          <Text style={styles.regular}>
            per the responses of the Owner per the questionnaire, a copy of
            which is annexed to this Agreement as Annexure 2 (
            <Text style={styles.bold}>“Questionnaire”</Text>).
          </Text>
        </View>
      </View>

      <View style={{ marginTop: 12 }}>
        <InfoRow label="Agreed Selling Price">
          {value(data.agreedSellingPrice)}
        </InfoRow>
      </View>

      <Text style={[styles.sectionHeading]}>Other Charges</Text>
      <InfoRow label="One Year Road Tax">
        {value(data.charges?.roadTax, "")}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="One Year Insurance">
        {value(data.charges?.insurance, "")}
      </InfoRow>
      <View style={styles.fieldGap} />
      <InfoRow label="Workshop/Accessories">
        {value(data.charges?.workshopAccessories, "")}
      </InfoRow>

      <View style={[styles.infoRow, { marginTop: 8 }]}>
        <Text style={styles.infoLabel}>Special Terms</Text>
        <Text style={styles.infoColon}>:</Text>
        <Text
          style={[styles.infoValue, { textAlign: "justify", fontWeight: 400 }]}
        >
          DCH may assist to settle the Owner’s loan for the Vehicle. In the
          event DCH assists with such settlement, DCH shall further be entitled
          to a service fee equivalent to 1% of the loan settlement sum, payable
          by the Owner upon issuance of a notice by DCH. In the event this
          Agreement is terminated for any reason whatsoever and/or in the event
          the Buyer fails to complete the purchase of the Vehicle for any reason
          whatsoever, DCH shall be entitled to demand for the return and refund
          of the loan settlement sum in full, from the Owner.
        </Text>
      </View>
      <PageNumber n="1" />
    </Page>
  );
}

function PageTwo() {
  return (
    <Page size="LETTER" style={[styles.page, styles.frontPage]}>
      <Text style={[styles.bold, { marginBottom: 10 }]}>GENERAL TERMS</Text>

      <Numbered no="1.">
        The vehicle owner as described in Section (A) above (“Owner”) is the
        owner of the vehicle with the descriptions outlined in Section (B) above
        (“Vehicle”).
      </Numbered>
      <Numbered no="2.">
        The Owner agrees to exclusively advertise and identify potential buyers
        for the Vehicle (“Buyer”) during a specified exclusivity period of three
        (3) months commencing from the date of this Agreement (“Exclusivity
        Period”) with DCH Automobile Sdn. Bhd. (Registration No. 202301046619
        (1540533-U)) (“DCH”). In this respect, the Owner acknowledges that DCH
        operates online and social media channels to facilitate the sale of
        second-hand vehicles. DCH shall advertise the Vehicle based on the
        descriptions provided by the Owner in Section (B) above.
      </Numbered>
      <Numbered no="3.">During the Exclusivity Period:</Numbered>
      <View>
        <Numbered no="(a)">
          the Owner shall not engage any other parties for the sale,
          advertisement or any dealings in connection with the Vehicle through
          any means at all; and
        </Numbered>
        <Numbered no="(b)">
          DCH shall be entitled to deal with the Vehicle in the manner set out
          below:
        </Numbered>
      </View>

      <View
        style={[
          styles.table,
          styles.serviceTable,
          { marginTop: 4, marginBottom: 7 },
        ]}
      >
        <View style={styles.tr}>
          <View style={[styles.th, { width: 58.45 }]}>
            <Text>Service</Text>
          </View>
          <View style={[styles.th, { width: 373.05 }]}>
            <Text>Details</Text>
          </View>
        </View>
        <View style={styles.tr}>
          <ServiceNameCell>Vehicle{`\n`}inspection</ServiceNameCell>
          <View style={[styles.td, styles.serviceDetailCell]}>
            <ServiceDetail marker="(a)">
              At the Owner’s request, DCH shall conduct an inspection on the
              Vehicle which includes but not limited to, assessment of the
              Vehicle’s mechanical and/or electrical condition, safety features
              and overall appearance. Following this, the Report will be
              produced and annexed to this Agreement.
            </ServiceDetail>
          </View>
        </View>
        <View style={styles.tr}>
          <ServiceNameCell>Marketing</ServiceNameCell>
          <View style={[styles.td, styles.serviceDetailCell]}>
            <ServiceDetail marker="(a)" style={{ marginBottom: 2 }}>
              DCH shall be given the mandate to:
            </ServiceDetail>
            <View style={{ marginLeft: 20 }}>
              <Bullet>
                Advertise the Vehicle on DCH’s online and social media channels;
              </Bullet>
              <Bullet>Identify and source for the Buyer; and</Bullet>
              <Bullet>
                Attend to negotiations and discussions with the Buyer.
              </Bullet>
            </View>
            <ServiceDetail marker="(b)" style={{ marginTop: 3 }}>
              DCH shall have the right to adjust the advertised price of the
              Vehicle on its online and social media platforms, as and when
              necessary. These adjustments may be made to reflect among others,
              any market fluctuations and Buyer’s interest or feedback.
              Correspondingly, the Agreed Selling Price shall be subject to
              variation.
            </ServiceDetail>
          </View>
        </View>
        <View style={styles.tr}>
          <ServiceNameCell>Others</ServiceNameCell>
          <View style={[styles.td, styles.serviceDetailCell]}>
            <ServiceDetail marker="(a)" style={{ marginBottom: 2 }}>
              DCH shall provide any additional services related to the Vehicle
              that are necessary or incidental to the sale of the Vehicle.
            </ServiceDetail>
            <ServiceDetail marker="(b)" style={{ marginBottom: 2 }}>
              To generally deal with the Vehicle, including to arrange for
              test-drives and viewing.
            </ServiceDetail>
            <ServiceDetail marker="(c)">
              To generally deal with the Vehicle as DCH may deem fit, in order
              to carry out and give effect to the Services (as hereinafter
              defined), including all matters incidental thereto.
            </ServiceDetail>
          </View>
        </View>
      </View>

      <Text style={{ marginLeft: 36, marginBottom: 10 }}>
        (The items listed in clause 3(b) above shall collectively be referred to
        as “Services”)
      </Text>
      <Numbered no="4.">
        The Owner agrees to pay DCH the following fees (“Service Fee”):
      </Numbered>

      <View style={[styles.table, styles.feeTable, { marginTop: 4 }]}>
        <View style={styles.tr}>
          <View style={[styles.th, { width: 25.3 }]}>
            <Text>No.</Text>
          </View>
          <View style={[styles.th, { width: 222.05 }]}>
            <Text>Service</Text>
          </View>
          <View style={[styles.th, { width: 92.1 }]}>
            <Text>Fee</Text>
          </View>
          <View style={[styles.th, { width: 92.05 }]}>
            <Text>Payment{`\n`}Timeframe</Text>
          </View>
        </View>
        <View style={styles.tr}>
          <FeeCell width={25.3}>(a)</FeeCell>
          <FeeCell width={222.05}>Vehicle inspection</FeeCell>
          <FeeCell width={92.1}>RM300</FeeCell>
          <FeeCell width={92.05}>Upon signing of this Agreement</FeeCell>
        </View>
        <View style={styles.tr}>
          <FeeCell width={25.3}>(b)</FeeCell>
          <FeeCell width={222.05}>
            Any additional services related to the Vehicle that are necessary or
            incidental to procure or facilitate the sale of the Vehicle,
            including to service, repair, restore, touch-up and such other
            necessary actions to restore the Vehicle to the agreed Vehicle State
            and Condition.
          </FeeCell>
          <FeeCell width={92.1}>
            To be prescribed and determined by DCHF
          </FeeCell>
          <FeeCell width={92.05}>Prior to commencement of the services</FeeCell>
        </View>
      </View>
      <PageNumber n="2" />
    </Page>
  );
}

function PageThree({ data }) {
  return (
    <Page size="LETTER" style={[styles.page, styles.frontPage]}>
      <View style={[styles.table, styles.feeTable, { marginBottom: 9 }]}>
        <View style={styles.tr}>
          <FeeCell width={25.3}>(c)</FeeCell>
          <FeeCell width={222.05}>
            If the Vehicle is sold during the Exclusivity Period through the
            efforts of DCH and through the outcome of the Services.
          </FeeCell>
          <FeeCell width={92.1}>
            Commission (3%) = 3% multiplied by the Agreed Selling Price
          </FeeCell>
          <FeeCell width={92.05}>
            Before the sale of the Vehicle is completed
          </FeeCell>
        </View>
        <View style={styles.tr}>
          <FeeCell width={25.3}>(d)</FeeCell>
          <FeeCell width={222.05}>
            If the Vehicle is sold at any time (during the Exclusivity Period)
            by means other than in accordance with Clause 4(c) above.
          </FeeCell>
          <FeeCell width={92.1}>
            Commission (1%) = 1% multiplied by the Agreed Selling Price
          </FeeCell>
          <FeeCell width={92.05}>
            Before the sale of the Vehicle is completed
          </FeeCell>
        </View>
      </View>

      <Text style={[styles.generalP, { marginBottom: 12 }]}>
        The Service Fee shall be non-refundable. DCH shall be entitled to
        set-off any amount owing to it (including but not limited to the Service
        Fee as set out in the table above) from the Agreed Selling Price or such
        other amounts held by DCH.
      </Text>
      <Numbered no="5.">
        DCH and the Owner further agree to be bound by the terms and conditions
        as included in the following page.
      </Numbered>

      <Text style={{ marginTop: 15, marginBottom: 2 }}>
        I, the undersigned, confirm that I have read the provisions printed on
        the front and back sides of this
      </Text>
      <Text>
        agreement and I accept all the terms and provisions of this Agreement.
      </Text>

      <View style={styles.signatureGrid}>
        <View style={styles.signatureCol}>
          <Text>DCH Automobile Sdn. Bhd.</Text>
          <View style={styles.signatureLine} />
          <Text>Name: VIVIAN VOON</Text>
          <Text>Designation: ADMIN</Text>
          <Text style={{ marginTop: 21 }}>Witness</Text>
          <View style={styles.signatureLine} />
          <Text>Name: KYLE WAN KHAI YANG</Text>
          <Text>NRIC No.: 910706-08-6217</Text>
        </View>
        <View style={styles.signatureColRight}>
          <Text>Owner</Text>
          <View style={styles.signatureLine} />
          <Text>Name: {value(data.owner?.name)}</Text>
          <Text>NRIC No.: {value(data.owner?.idNo)}</Text>
          <Text style={{ marginTop: 21 }}>Witness</Text>
          <View style={styles.signatureLine} />
          <Text>Name: {value(data.salesAgent)}</Text>
          <Text>NRIC No.:</Text>
        </View>
      </View>
      <PageNumber n="3" />
    </Page>
  );
}

function PageFour() {
  return (
    <Page size="LETTER" style={[styles.page, styles.legalPage]}>
      <Text
        style={[styles.title, { width: 252, fontSize: 9, marginBottom: 9 }]}
      >
        Terms and Conditions
      </Text>
      <View style={styles.twoCol}>
        <View style={styles.column}>
          <Text style={styles.legalSection}>1. TERM</Text>
          <LegalNumber no="1.1">
            This Agreement shall commence from the date of execution of this
            Agreement and shall continue until the earlier of:-
          </LegalNumber>
          <LegalP>(a) upon expiry of the Exclusivity Period;</LegalP>
          <LegalP>
            (b) the successful sale and transfer of ownership of the Vehicle to
            DCH; or
          </LegalP>
          <LegalP>
            (c) termination of this Agreement in accordance with clause 5 below.
          </LegalP>
          <LegalP>(collectively referred to as “Term”)</LegalP>

          <Text style={styles.legalSection}>2. RIGHTS AND DUTIES</Text>
          <LegalNumber no="2.1">
            The Owner agrees and undertakes that:
          </LegalNumber>
          <LegalP>
            (a) the Owner shall provide accurate and complete information about
            the Vehicle, including its condition, history, service records, and
            any defects or required repairs;
          </LegalP>
          <LegalP>
            (b) the Agreed Selling Price shall be premised on the Vehicle’s
            overall condition, safety features, overall appearance and the
            applicable market rate for similar vehicles;
          </LegalP>
          <LegalP>
            (c) the Owner shall deliver the Vehicle to DCH within three (3) days
            upon notification from DCH for the purposes relating to the
            inspection or marketing services;
          </LegalP>
          <LegalP>
            (d) the Owner shall leave the Vehicle with DCH for such duration as
            may be prescribed by DCH;
          </LegalP>
          <LegalP>
            (e) the Owner shall provide all necessary documents and do all
            things necessary to facilitate the sale of the Vehicle in accordance
            with Clause 3 below. This includes, but is not limited to:-
          </LegalP>
          <LegalP>
            (i) cooperating with DCH and/or Puspakom in facilitating inspections
            of the Vehicle;
          </LegalP>
          <LegalP>
            (ii) providing biometrics authentication to the Road Transport
            Department (Jabatan Pengangkutan Jalan) or platform prescribed by
            DCH;
          </LegalP>
          <LegalP>
            (iii) complying with such other directions and instructions provided
            by DCH.
          </LegalP>
          <LegalP>
            (f) the Owner is to ensure that the Vehicle is available for
            inspection, viewings, and test drives as reasonably requested by DCH
            during the Term of this Agreement;
          </LegalP>
          <LegalP>
            (g) premised on the information and documents provided by the Owner
            regarding the state and condition of the Vehicle as well as the
            inspection services carried out by DCH, DCH shall in its advertising
            and marketing set out clearly the state and condition of the Vehicle
            as described in Section B above (“Vehicle State & Condition”).
            Notwithstanding the aforementioned, it remains the Owner’s
            responsibility and obligation to ensure the accuracy of the Vehicle
            State & Condition. In this regard, the inspection services carried
            out by DCH and the Report is not guaranteed to provide a
            comprehensive analysis of the state and condition of the Vehicle.
          </LegalP>
          <LegalP>
            (h) in the event DCH identifies a Buyer, DCH shall inform the Owner.
            Upon identification of the Buyer, the provisions set out in Clause 3
            below shall apply.
          </LegalP>

          <Text style={styles.legalSection}>3. SALE TO BUYER</Text>
          <LegalNumber no="3.1">
            Upon identification of the Buyer for the Vehicle, DCH shall have the
            following options:
          </LegalNumber>
          <LegalP>
            (a) purchase the Vehicle from the Owner at the Agreed Selling Price
            (as hereinafter defined); or
          </LegalP>
          <LegalP>
            (b) compel the Owner to sell the Vehicle to the Buyer at the Agreed
            Selling Price (as hereinafter defined); or
          </LegalP>
          <LegalP>(c) do nothing.</LegalP>
        </View>

        <View style={styles.column}>
          <LegalNumber no="3.2">
            If DCH elects option 3.1(a), the Owner shall sell the Vehicle to
            DCH, or other entity nominated by DCH at the agreed selling price as
            stipulated in Section (B) above (“Agreed Selling Price”) and the
            following shall take place:
          </LegalNumber>
          <LegalP>
            (a) The Owner shall deliver the Vehicle to DCH within three (3) days
            along with the original vehicle grant or vehicle ownership
            certificate, upon receipt of notification from DCH.
          </LegalP>
          <LegalP>
            (b) Once delivered, DCH shall inspect the Vehicle. If the Vehicle is
            not in Vehicle State & Condition, DCH shall have the right to reject
            the Vehicle, vary the Agreed Selling Price or attend to the
            necessary repairs and/or replacements required to reinstate the
            Vehicle to the Vehicle State & Condition, all at the costs of the
            Owner.
          </LegalP>
          <LegalP>
            (c) Subject always to Clause 3.2(b) above, DCH shall be entitled to
            prescribe a document for the sale of the Vehicle (“Vehicle Purchase
            Agreement”), which form and substance shall be prescribed by DCH and
            executed by the Owner upon request.
          </LegalP>
          <LegalP>
            (d) Subject to the terms of the Vehicle Purchase Agreement (if any),
            the Owner shall transfer the ownership of the Vehicle to DCH or the
            Buyer in the manner and timeline prescribed by DCH (“Vehicle
            Ownership Transfer”), in exchange for DCH’s payment of 10% of the
            Agreed Selling Price (“Initial Payment”). Subject always to DCH’s
            receipt of the Agreed Selling Price from the Buyer or the Buyer’s
            financier, DCH shall thereafter pay the Agreed Selling Price to the
            Owner (less the Initial Payment). Subject always to the foregoing,
            it is intended but not guaranteed that the Agreed Selling Price
            (less the Initial Payment) will be paid to the Owner within 15
            Business Days from the date the Vehicle Ownership Transfer is
            successfully completed.
          </LegalP>
          <LegalP>
            (e) DCH shall be entitled to set-off any amount owing to it
            (including but not limited to the Service Fee) from the Agreed
            Selling Price, before remitting the balance to the Owner.
          </LegalP>
          <LegalP>
            (f) If the Owner fails, neglects or refuses to carry out and
            complete the Vehicle Ownership Transfer in the manner and timeline
            prescribed by DCH, the Owner shall pay DCH a penalty amount at the
            rate of 0.1% of the Agreed Selling Price or Ringgit Malaysia Two
            Hundred (RM200) (whichever is the higher) per day for each day of
            delay in transferring the ownership of the Vehicle, starting from
            the first day after the notice was given and continuing until the
            date the transfer of ownership is completed. DCH and the Owner agree
            that the penalty is a reasonable estimate of the potential losses
            and damages that DCH may suffer as a result of the Owner’s failure
            to transfer the Vehicle in accordance with this Agreement.
          </LegalP>
          <LegalP>
            (g) If for whatsoever reason, the Buyer fails or refuses to complete
            the purchase of the Vehicle from DCH, DCH shall be entitled to
            return and sell the Vehicle back to the Owner at the Agreed Selling
            Price. Thereafter, the Owner shall take necessary actions to
            re-transfer ownership and registration of the Vehicle to his name.
            Further, the Owner shall refund all monies paid by DCH and/or the
            Buyer to the Owner, within 24 hours from the date of demand by DCH.
          </LegalP>
          <LegalNumber no="3.3">
            If DCH elects option 3.1(b), the Owner shall sell the Vehicle to the
            Buyer at the Agreed Selling Price and the following shall take
            place:
          </LegalNumber>
          <LegalP>
            (a) Clauses 3.2(a) to 3.2(c) shall apply. In this circumstance, DCH
            shall have no liability and obligations under any agreement entered
            between the Owner and the Buyer.
          </LegalP>
          <LegalP>
            (b) Subject to the terms of the Vehicle Purchase Agreement (if any),
            the Owner shall carry out and complete the Vehicle Ownership
            Transfer in the manner and timeline prescribed by DCH, in return of
            which the Buyer shall pay the Agreed Selling Price to the Owner.
          </LegalP>
          <LegalP>
            (c) If the Owner fails, neglects or refuses to carry out and
            complete the Vehicle Ownership Transfer in the manner and timeline
            prescribed by DCH, the Owner shall pay DCH a penalty amount at the
            rate of 0.1% of the Agreed Selling Price or Ringgit Malaysia Two
            Hundred (RM200) (whichever is the higher) per day for each day of
            delay in transferring the ownership of the Vehicle, starting from
            the first day after the notice was given and continuing until the
            date the transfer of ownership is completed. DCH and the Owner agree
            that the penalty is a reasonable estimate of the potential losses
            and damages that DCH may suffer as a result of the Owner’s failure
            to transfer the Vehicle in accordance with this Agreement.
          </LegalP>

          <Text style={styles.legalSection}>
            4. REPRESENTATION AND WARRANTIES
          </Text>
          <LegalNumber no="4.1">
            The Owner represents, undertakes and warrants that:
          </LegalNumber>
          <LegalP>
            (a) it will follow the Agreed Selling Price for the Vehicle. In the
            event DCH informs the Owner in writing that there is a Buyer for the
            Vehicle, the Owner shall be bound to sell the Vehicle to DCH, if so
            elected by DCH;
          </LegalP>
          <LegalP>
            (b) upon notification that a Buyer has been identified, the Owner
            shall immediately cease the usage of the Vehicle and shall
            thereafter transfer temporary ownership of the Vehicle to DCH, in
            such manner prescribed by DCH;
          </LegalP>
          <LegalP>
            (c) it is the legal, beneficial and registered owner of the Vehicle;
          </LegalP>
        </View>
      </View>
      <PageNumber n="4" />
    </Page>
  );
}

function PageFive() {
  return (
    <Page size="LETTER" style={[styles.page, styles.legalPage]}>
      <View style={styles.twoCol}>
        <View style={styles.column}>
          <LegalP>
            (d) the Owner represents, warrants and undertakes that the Vehicle
            State & Condition is true, complete, accurate, not misleading and
            contains no material omission;
          </LegalP>
          <LegalP>
            (e) despite the Report being produced by DCH, the Owner is
            responsible for its contents and represents, warrants and undertakes
            that the Report is true, complete, accurate, not misleading and
            contains no material omission;
          </LegalP>
          <LegalP>
            (f) to complete the questionnaire, a copy of which is annexed to
            this Agreement as Annexure 2 (“Questionnaire”). The Owner
            represents, warrants and undertakes that the Questionnaire is true,
            complete, accurate, not misleading and contains no omission;
          </LegalP>
          <LegalP>
            (g) it will ensure that the Vehicle is delivered to DCH in the same
            Vehicle State & Condition (reasonable and fair wear and tear
            excepted) within three (3) days upon receipt of notification from
            DCH;
          </LegalP>
          <LegalP>
            (h) the Vehicle is free and will be free from all liens,
            encumbrances, charges and security interests. If the Vehicle is
            under an unsettled loan, the Owner shall fully settle the loan at
            the Owner’s own cost and expenses before the sale of the Vehicle to
            DCH is finalised;
          </LegalP>
          <LegalP>
            (i) the Vehicle has not been involved in any accidents and has not
            sustained any damages due to flooding;
          </LegalP>
          <LegalP>
            (j) the mileage of the Vehicle is genuine and has not been altered
            or adjusted in any manner;
          </LegalP>
          <LegalP>
            (k) all applicable custom duties, taxes and all other fees payable
            to the governmental authorities in relation to the Vehicle have been
            paid and settled in full;
          </LegalP>
          <LegalP>
            (l) the Vehicle is in good working condition and has been accurately
            described in Section (B) above, including all known defects,
            damages, or required repairs;
          </LegalP>
          <LegalP>
            (m) all fines and compounds imposed by Polis Diraja Malaysia, local
            authority and any other enforcement, regulatory and governmental
            authority have been paid and settled in full;
          </LegalP>
          <LegalP>
            (n) it is part of the Vehicle State & Condition that all
            representations made by the Owner regarding the condition, history
            and specifications of the Vehicle either as stated in Section (B)
            above or communicated to DCH are true, complete, accurate and
            contain no material omission;
          </LegalP>
          <LegalP>
            (o) the Vehicle complies with all applicable laws, regulations and
            standards, including safety, environmental and emissions
            regulations;
          </LegalP>
          <LegalP>
            (p) the Owner has obtained all necessary licenses, permits and
            approvals required for the sale and transfer of the Vehicle;
          </LegalP>
          <LegalP>
            (q) the Owner has disclosed all material information regarding the
            Vehicle to DCH, and no information has been withheld that would
            affect the sale or valuation of the Vehicle;
          </LegalP>
          <LegalP>
            (r) it has the power and capacity to execute, deliver and perform
            the terms of this Agreement without violating any law or regulation;
          </LegalP>
          <LegalP>
            (s) it is not bankrupt and/or wound up, as the case may be, at the
            time of execution of this Agreement nor is there any insolvency
            proceedings which are current, pending or threatened against it;
          </LegalP>
          <LegalP>(t) the Vehicle is adequately insured;</LegalP>
          <LegalP>
            (u) the road tax for the Vehicle has been paid and settled in full;
          </LegalP>
          <LegalP>
            (v) it is not in default under any agreement to which it is a party
            or by which it may be bound and no litigation, arbitration or
            administrative proceedings are presently current or pending or
            threatened which default, litigation, arbitration or administrative
            proceedings, as the case may be, might materially affect its
            solvency or might affect its ability to perform its obligations
            under this Agreement;
          </LegalP>
          <LegalP>
            (w) to provide full cooperation and assistance to DCH in relation to
            the sale of the Vehicle, including but not limited to responding
            promptly to all requests for information, signing and executing all
            documents, and delivering any materials or records related to the
            Vehicle as may be required to complete the sale process; and
          </LegalP>
          <LegalP>
            (x) this Agreement constitutes or when executed will constitute its
            legal, valid and binding obligations in accordance with the terms
            hereof.
          </LegalP>
          <LegalNumber no="4.2">
            The Owner warrants that the above representations and warranties
            will be fulfilled down to and will be true and correct throughout
            the Term in all aspects. The Owner undertakes to immediately inform
            DCH by a written notice in the event of breach of any of the above
            representations or warranties.
          </LegalNumber>
        </View>
        <View style={styles.column}>
          <LegalNumber no="4.3">
            The Owner shall indemnify, defend and hold harmless DCH, its
            affiliates, officers, directors, employees, agents, and
            representatives (collectively, the “Indemnified Parties”) from and
            against any and all claims, demands, actions, suits, proceedings,
            liabilities, damages, losses, costs, and expenses arising out of or
            in connection with any breach of the representations and warranties
            set forth in this Clause.
          </LegalNumber>
          <LegalNumber no="4.4">
            No warranty and representation is provided by DCH with respect to
            the chances of success for a successful sale of the Vehicle.
          </LegalNumber>

          <Text style={styles.legalSection}>5. TERMINATION</Text>
          <LegalNumber no="5.1">
            DCH shall be entitled to terminate this Agreement:-
          </LegalNumber>
          <LegalP>
            (a) by seven (7) days written notice to the Owner, without providing
            any reason to the Owner;
          </LegalP>
          <LegalP>
            (b) if the Owner commits a material breach, or a series of breaches
            resulting in a material breach, of the Agreement and fails to remedy
            such breaches in the period prescribed by DCH; or
          </LegalP>
          <LegalP>
            (c) immediately, if the Owner fails to comply with applicable laws
            and regulations related to the sale and transfer of the Vehicle.
          </LegalP>
          <LegalNumber no="5.2">
            Upon termination of this Agreement, if the Vehicle is in the
            possession of DCH at the time of termination, the Owner shall
            arrange to collect the Vehicle, in such manner prescribed by DCH.
            The Owner is responsible for arranging and covering any costs
            associated with the transportation of the Vehicle unless decided
            otherwise by DCH.
          </LegalNumber>
          <LegalNumber no="5.3">
            Termination shall be without prejudice to the accrued rights and
            liabilities of the Parties and any obligations which survive
            termination under this Agreement.
          </LegalNumber>

          <Text style={styles.legalSection}>6. CANCELLATION</Text>
          <LegalNumber no="6.1">
            The Owner shall not be entitled to cancel or withdraw the sale of
            the Vehicle during Exclusivity Period and/or when DCH has identified
            a Buyer for the Vehicle.
          </LegalNumber>
          <LegalNumber no="6.2">
            Notwithstanding Clause 6.1, DCH shall be entitled to cancel the sale
            of the Vehicle during Exclusivity Period and/or the Term of this
            Agreement, as the case may be, if it is found that the condition of
            the Vehicle upon delivered to DCH, has significantly deteriorated
            and/or substantially different from the original condition of the
            Vehicle when DCH conducted its initial inspection. Such changes may
            include, but are not limited to, severe mechanical or electrical
            failures, severe damages sustained in accidents, unauthorised
            modifications and/or any other alterations that may adversely affect
            the Vehicle’s value, safety or marketability.
          </LegalNumber>
          <LegalNumber no="6.3">
            For the avoidance of doubt, a cancellation under this clause shall
            not constitute a termination of the entire Agreement, which shall
            remain in effect for other provisions and potential future sales,
            unless separately terminated under Clause 5.
          </LegalNumber>

          <Text style={styles.legalSection}>7. RISK</Text>
          <LegalNumber no="7.1">
            DCH shall not be liable for any loss, theft, damage, or destruction
            to the Vehicle while the Vehicle is in its custody, during the Term
            of this Agreement.
          </LegalNumber>
          <LegalNumber no="7.2">
            The risk of loss, theft, damage, or destruction to the Vehicle shall
            remain with the Owner until the ownership of the Vehicle has been
            transferred to DCH.
          </LegalNumber>

          <Text style={styles.legalSection}>8. INDEMNITY</Text>
          <LegalNumber no="8.1">
            The Owner shall indemnify, defend, and hold harmless the Indemnified
            Parties from and against any and all claims, demands, actions,
            suits, proceedings, liabilities, damages, losses, costs, and
            expenses arising out of or in connection with:-
          </LegalNumber>
          <LegalP>
            (a) any breach by the Owner of any representation, warranty,
            covenant, or obligation under this Agreement;
          </LegalP>
          <LegalP>
            (b) any claims arising from or relating to the condition, ownership
            or history of the Vehicle regardless of whether such claims arise
            during or after the term of this Agreement;
          </LegalP>
          <LegalP>
            (c) any failure by the Owner to comply with any applicable laws,
            regulations related to the sale and transfer of the Vehicle; and
          </LegalP>
          <LegalP>
            (d) any act or omission of the Owner, its employees, agents, or
            subcontractors in connection with the performance of this Agreement.
          </LegalP>
        </View>
      </View>
      <PageNumber n="5" />
    </Page>
  );
}

function PageSix() {
  return (
    <Page size="LETTER" style={[styles.page, styles.legalPage]}>
      <View style={styles.twoCol}>
        <View style={styles.column}>
          <LegalNumber no="8.2">
            The rights and remedies provided in this clause are in addition to,
            and not in lieu of, any other rights and remedies available to DCH
            at law or in equity.
          </LegalNumber>
          <LegalNumber no="8.3">
            The indemnity obligations set forth in this clause shall survive the
            termination or expiration of this Agreement.
          </LegalNumber>

          <Text style={styles.legalSection}>9. MISCELLANEOUS</Text>
          <LegalNumber no="9.1">
            <Text style={styles.bold}>Limitation of Liability:</Text> To the
            maximum extent permitted by law, DCH’s total liability to the Owner
            for any and all claims, losses, costs, or damages arising out of or
            related to this Agreement, whether in contract, tort (including
            negligence), or otherwise, shall not exceed the Service Fee paid to
            DCH and received by DCH. In no event shall DCH be liable to the
            Owner for any indirect, incidental, consequential, special, or
            punitive damages, including but not limited to loss of profits, loss
            of business opportunities, loss of goodwill, or business
            interruption, arising out of or in connection with this Agreement,
            even if DCH has been advised of the possibility of such damages.
          </LegalNumber>
          <LegalNumber no="9.2">
            <Text style={styles.bold}>Time:</Text> Time whenever mentioned is
            the essence of this Agreement, both as regards the date and period
            specifically mentioned and as to any dates and periods which may be
            agreed in writing between the Parties.
          </LegalNumber>
          <LegalNumber no="9.3">
            <Text style={styles.bold}>Amendment:</Text> Any waiver, modification
            or amendment made by either Party to this Agreement shall not be
            effective unless made in writing and duly signed by each of the
            Parties hereto.
          </LegalNumber>
          <LegalNumber no="9.4">
            <Text style={styles.bold}>Severability:</Text> The unenforceability
            of any part of this Agreement shall not affect the enforceability of
            any other part.
          </LegalNumber>
          <LegalNumber no="9.5">
            <Text style={styles.bold}>Notice:</Text>
          </LegalNumber>
          <LegalP>
            (a) All notices, requests or demands (each a notice) required to be
            given, made or served for any purpose under this Agreement shall
            either be delivered personally to the person to whom it is
            addressed, or left at or sent by A.R. registered post to the
            person’s address or emailed in accordance with the addressee’s email
            or sent by WhatsApp or other mobile messaging service prescribed by
            DCH. The details of DCH are set out below while the details of the
            Owner are set out in this Agreement.
          </LegalP>
          <LegalP>
            DCH Automobile SDN. BHD. (Registration No. 202301046619 (1540533-U))
          </LegalP>

          <View
            style={[
              styles.table,
              styles.noticeTable,
              { marginVertical: 4, fontSize: 8 },
            ]}
          >
            <View style={styles.tr}>
              <View style={[styles.td, styles.noticeLabel]}>
                <Text>Registered{`\n`}Address</Text>
              </View>
              <View style={[styles.td, { flex: 1 }]}>
                <Text>
                  No. 7-4, Jalan USJ 9/5Q, Subang Business Centre, UEP Subang
                  Jaya, 47620 Subang Jaya, Selangor
                </Text>
              </View>
            </View>
            <View style={styles.tr}>
              <View style={[styles.td, styles.noticeLabel]}>
                <Text>Business{`\n`}Address</Text>
              </View>
              <View style={[styles.td, { flex: 1 }]}>
                <Text>
                  No. 7, Jalan Astaka 1F/KU2, Bandar Bukit Raja, 41050 Klang,
                  Selangor
                </Text>
              </View>
            </View>
            <View style={styles.tr}>
              <View style={[styles.td, styles.noticeLabel]}>
                <Text>Email</Text>
              </View>
              <View style={[styles.td, { flex: 1 }]}>
                <Text>admin_dchautomobile@dhub.my</Text>
              </View>
            </View>
            <View style={styles.tr}>
              <View style={[styles.td, styles.noticeLabel]}>
                <Text>Attention to</Text>
              </View>
              <View style={[styles.td, { flex: 1 }]}>
                <Text>Mr. Kyle Wan / Ms. Vivian Voon</Text>
              </View>
            </View>
          </View>

          <LegalP>
            (b) A notice is taken as given by a Party (“Sender”) and received by
            the other Party (“Recipient”) if:
          </LegalP>
          <LegalP>(i) by hand delivery, upon delivery;</LegalP>
          <LegalP>
            (ii) by A.R. registered post, five (5) Business Days from the date
            of postage;
          </LegalP>
          <LegalP>
            (iii) by courier service, at the time of the Recipient’s receipt;
          </LegalP>
          <LegalP>
            (iv) by electronic mail, at the time of transmission as shown on the
            date stamp of the electronic mail; or
          </LegalP>
          <LegalP>
            (v) if sent by WhatsApp or other mobile messaging service, at the
            time the message is delivered and read, as evidenced by a delivery
            and read receipt from the application.
          </LegalP>
          <LegalP>
            (c) But if delivery or receipt is on a day which is not a Business
            Day or is after 5.00 pm at the place of delivery or receipt, it is
            deemed received at 9.00 am on the next Business Day. For the
            avoidance of doubt, in the context of this Agreement, ‘Business Day’
            means a day when banks are opened for business in the State of
            Selangor, other than a Saturday, Sunday or gazetted public holiday
            in the State of Selangor.
          </LegalP>
        </View>

        <View style={styles.column}>
          <LegalNumber no="9.6">
            <Text style={styles.bold}>Waiver:</Text>
          </LegalNumber>
          <LegalP>
            (a) A Party waives a right under this Agreement only if it does so
            in writing.
          </LegalP>
          <LegalP>(b) A Party does not waive a right simply because it:</LegalP>
          <LegalP>(i) fails to exercise the right;</LegalP>
          <LegalP>(ii) delays in exercising the right; or</LegalP>
          <LegalP>(iii) only exercise part of the right.</LegalP>
          <LegalP>
            (c) A waiver of one breach of a term of this Agreement does not
            operate as a waiver of another breach of the same term or any other
            term.
          </LegalP>
          <LegalNumber no="9.7">
            <Text style={styles.bold}>Successors and Assigns:</Text> This
            Agreement shall be binding upon and shall inure to the benefit of
            the Parties and their respective successors and permitted assigns.
          </LegalNumber>
          <LegalNumber no="9.8">
            <Text style={styles.bold}>Cost and Expense:</Text> The Owner shall
            pay all duty chargeable and payable pursuant to the Stamp Act 1949
            including but not limited to Section 4(3) of the Stamp Act 1949. If
            the Owner fails, refuses or neglects to stamp this Agreement in
            accordance with the Stamp Act 1949 in a timely manner and to forward
            a copy of the stamped Agreement to DCH, the Owner agrees that it is
            precluded from raising the argument in the Courts of Malaysia that
            this Agreement is inadmissible in evidence in accordance with
            Section 52 of the Stamp Act 1949. Save for the foregoing, each Party
            shall pay its own costs and disbursements of and incidental to the
            negotiation, preparation and completion of this Agreement.
          </LegalNumber>
          <LegalNumber no="9.9">
            <Text style={styles.bold}>Entire Agreement:</Text> This Agreement
            replaces any previous agreement, representation, warranty or
            understanding between the Parties concerning the subject matter and
            contains the entire agreement between the Parties. This Agreement
            embodies the entire agreement and understanding of the Parties and
            there are no provisions, terms, conditions or obligations, oral or
            written, express or implied other than those contained herein and in
            the event of this Agreement being found inconsistent with any other
            document or documents signed by them prior to the execution of this
            Agreement, the terms of this Agreement shall prevail. For the
            avoidance of doubt, all earlier rights of the Parties prior to this
            Agreement are extinguished.
          </LegalNumber>
          <LegalNumber no="9.10">
            <Text style={styles.bold}>Counterparts:</Text> This Agreement may be
            executed in any number of counterparts and by different Parties on
            separate counterparts each of which when executed and delivered
            shall constitute an original, but all the counterparts shall
            together constitute one and the same instrument.
          </LegalNumber>
          <LegalNumber no="9.11">
            <Text style={styles.bold}>Governing Law and Jurisdiction:</Text>{" "}
            This Agreement shall be governed by and construed in accordance with
            the laws of Malaysia. Any dispute, controversy or claim arising out
            of or in connection with this Agreement, or the breach, termination
            or invalidity thereof, shall be finally submitted and settled by the
            Courts of Malaysia.
          </LegalNumber>
          <LegalNumber no="9.12">
            <Text style={styles.bold}>Further Assurance:</Text> The Parties
            shall do all further acts and execute all further documents
            necessary to give effect to this Agreement.
          </LegalNumber>
          <LegalNumber no="9.13">
            <Text style={styles.bold}>References to Clauses:</Text> References
            to clauses, sections, schedules, and appendices, unless otherwise
            specified, are to clauses, sections, schedules, and appendices of
            this Agreement, and the same shall form part and parcel of this
            Agreement.
          </LegalNumber>
          <LegalNumber no="9.14">
            <Text style={styles.bold}>Headings:</Text> All headings,
            sub-headings, and the table of contents herein are for reference
            purposes only and do not form part of this Agreement and shall not
            be deemed to alter or affect the meaning of any provisions hereof.
          </LegalNumber>
          <LegalNumber no="9.15">
            <Text style={styles.bold}>Persons and Assigns:</Text> Words denoting
            persons shall include their respective heirs, personal
            representatives, successors-in-title, or permitted assigns, as the
            case may be.
          </LegalNumber>
          <LegalNumber no="9.16">
            <Text style={styles.bold}>Construction Rules:</Text> No rule of
            construction shall apply to the detriment of either Party by reason
            of that Party having control and/or being responsible for the
            preparation of this Agreement or any part thereof.
          </LegalNumber>
          <LegalNumber no="9.17">
            <Text style={styles.bold}>References to Agreement:</Text> References
            to “this Agreement” or any other agreement or document shall be
            construed as references to such agreement or document as amended,
            modified, or supplemented and in effect from time to time and shall
            include references to any document which amends, modifies, or
            supplements it, or is entered into, made, or given pursuant to or in
            accordance with its terms.
          </LegalNumber>
        </View>
      </View>
      <PageNumber n="6" />
    </Page>
  );
}

function PageSeven() {
  return (
    <Page size="LETTER" style={styles.page}>
      <Text style={{ marginTop: 3 }}>
        [The remainder of this page is intentionally left blank]
      </Text>
      <PageNumber n="7" />
    </Page>
  );
}

function PageEight() {
  return (
    <Page size="LETTER" style={styles.page}>
      <Text style={[styles.bold, styles.centered, { marginTop: 4 }]}>
        Annexure 1
      </Text>
      <Text style={[styles.centered, { marginTop: 10 }]}>
        [To insert the vehicle inspection report]
      </Text>
      <PageNumber n="8" />
    </Page>
  );
}

function PageNine({ data }) {
  const vehicle = data.vehicle || {};
  return (
    <Page size="LETTER" style={[styles.page, styles.questionnairePage]}>
      <Text style={[styles.bold, styles.centered, { marginTop: 4 }]}>
        Annexure 2
      </Text>
      <Text style={[styles.bold, styles.centered, { marginTop: 7 }]}>
        Questionnaire
      </Text>

      <Text style={styles.qHeading}>Vehicle Description</Text>
      <QuestionnaireItem
        n="1"
        text={`Make and Model: ${value(vehicle.make)} ${value(vehicle.model)}`}
      />
      <QuestionnaireItem
        n="2"
        text={`Vehicle Registration Number: ${value(vehicle.registrationNo)}`}
      />
      <QuestionnaireItem
        n="3"
        text={`Year of Manufacture: ${value(vehicle.yearOfManufacture)}`}
      />
      <QuestionnaireItem
        n="4"
        text={`Mileage (odometer reading): ${value(vehicle.mileage, "")}`}
      />

      <Text style={styles.qHeading}>Vehicle History</Text>
      <QuestionnaireItem
        n="1"
        text="Has the Vehicle been involved in any accidents? (Yes/No)"
        sub="If Yes, please provide details:"
      />
      <QuestionnaireItem
        n="2"
        text="Has the Vehicle sustained any damage due to flooding? (Yes/No)"
        sub="If Yes, please describe:"
      />
      <QuestionnaireItem
        n="3"
        text="Has the mileage of the Vehicle ever been tampered with or adjusted? (Yes/No)"
        sub="If Yes, please provide details:"
      />
      <QuestionnaireItem
        n="4"
        text="Are there any existing liens, encumbrances, or loans attached to the Vehicle? (Yes/No)"
        sub="If Yes, please provide details:"
      />

      <Text style={styles.qHeading}>Mechanical and Electrical Condition</Text>
      <QuestionnaireItem
        n="1"
        text="Are there any known issues with the engine or transmission? (Yes/No)"
        sub="If Yes, please describe:"
      />
      <QuestionnaireItem
        n="2"
        text="Are all electrical components (e.g., lights, windows, radio) fully operational? (Yes/No)"
        sub="If No, please provide details:"
      />
      <QuestionnaireItem
        n="3"
        text="Are there any warning lights currently active on the dashboard? (Yes/No)"
        sub="If Yes, specify which lights and provide details:"
      />
      <QuestionnaireItem
        n="4"
        text="Has the Vehicle undergone any major repairs or part replacements? (Yes/No)"
        sub="If Yes, please provide details and dates:"
      />

      <Text style={styles.qHeading}>Exterior Condition</Text>
      <QuestionnaireItem
        n="1"
        text="Are there any visible dents, scratches, or rust on the Vehicle? (Yes/No)"
        sub="If Yes, please describe the location and extent:"
      />
      <QuestionnaireItem
        n="2"
        text="Are all the windows and mirrors intact and free from cracks or damage? (Yes/No)"
        sub="If No, please provide details:"
      />
      <QuestionnaireItem
        n="3"
        text="Is the paintwork original, or has it been repainted? (Original/Repainted)"
        sub="If repainted, please provide details:"
      />

      <Text style={styles.qHeading}>Interior Condition</Text>
      <QuestionnaireItem
        n="1"
        text="Is the upholstery in good condition, free from tears, stains, or damage? (Yes/No)"
        sub="If No, please describe:"
      />
      <QuestionnaireItem
        n="2"
        text="Are all dashboard components functional? (Yes/No)"
        sub="If No, please describe:"
      />
      <QuestionnaireItem
        n="3"
        text="Are the air conditioning and heating systems fully operational? (Yes/No)"
        sub="If No, please provide details:"
      />

      <Text style={styles.qHeading}>Tyres and Wheels</Text>
      <QuestionnaireItem
        n="1"
        text="Are the tyres in good condition with sufficient tread depth? (Yes/No)"
        sub="If No, please specify which tyres need replacement:"
      />
      <QuestionnaireItem
        n="2"
        text="Have the wheels or rims suffered any damage? (Yes/No)"
        sub="If Yes, please describe:"
      />

      <Text style={styles.qHeading}>Additional Information</Text>
      <QuestionnaireItem
        n="1"
        text="Has the Vehicle been modified or altered in any way from its original specifications? (Yes/No)"
        sub="If Yes, please describe:"
      />
      <QuestionnaireItem
        n="2"
        text="Are there any other defects or issues not previously mentioned? (Yes/No)"
        sub="If Yes, please provide details:"
      />

      <Text style={[styles.bold, { marginTop: 13, marginBottom: 4 }]}>
        Declaration
      </Text>
      <Text>
        I, the undersigned, declare that all the information provided in this
        questionnaire is true, accurate, and contains no omissions.
      </Text>
      <Text style={{ marginTop: 13 }}>Owner's Signature</Text>
      <Text style={{ marginTop: 5 }}>Date: {value(data.date)}</Text>
      <PageNumber n="9" />
    </Page>
  );
}

export default function ServiceAgreementPdf({
  data = serviceAgreementSampleData,
}) {
  return (
    <Document
      title={serviceAgreementFileName.replace(/\.pdf$/i, "")}
      author="DCH Automobile Sdn. Bhd."
    >
      <PageOne data={data} />
      <PageTwo />
      <PageThree data={data} />
      <PageFour />
      <PageFive />
      <PageSix />
      <PageSeven />
      <PageEight />
      <PageNine data={data} />
    </Document>
  );
}
